import React, { useState, useRef, useEffect, useCallback } from 'react';
import { restoreposicaoCursor, getTextBeforeCursor, calculaPosicaoCursor } from './utils/cursorManager';
import { processMentionHTML, detectMentionQuery, extractTextWithMentions } from './utils/mentionParser';
import { useMentionSearch } from './hooks/useMentionSearch';
import MentionDropdown from './components/MentionDropdown';
import './mentions.css';

const Mentions = ({ 
  value = '', 
  onChange, 
  disabled = false,
  style,
  currentUserId = null,
  ...props
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const editorRef = useRef(null);
  
  const { 
    suggestions, 
    searchWithDebounce, 
    clearSuggestions 
  } = useMentionSearch();

  // Extrai texto com formato correto para salvar no banco
  const getTextForSave = useCallback(() => {
    if (!editorRef.current) return '';
    return extractTextWithMentions(editorRef.current).split('\u0001').join('');
  }, []);

  useEffect(() => {
    if (props.onGetFormattedValue) {
      props.onGetFormattedValue(getTextForSave);
    }
  }, [getTextForSave, props]);


  // Detecta menções durante digitação
  const detectMention = useCallback(() => {
    const cursorData = getTextBeforeCursor();
    if (!cursorData) return;

    const mentionData = detectMentionQuery(cursorData.text);
    
    if (mentionData) {
      setShowSuggestions(true);
      setSelectedIndex(0);
      searchWithDebounce(mentionData.query);
    } else {
      setShowSuggestions(false);
      clearSuggestions();
    }
  }, [searchWithDebounce, clearSuggestions]);

  // Insere a menção selecionada
  const insertMention = useCallback((collaborator) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    // pega o texto atual formatado completo
    const fullText = extractTextWithMentions(editorRef.current);
    
    // Calcular posição do cursor no texto completo
    const range = selection.getRangeAt(0);
    const posicaoCursor = calculaPosicaoCursor(editorRef.current, range);
    
    // se não achou o cursor, retorna
    if (posicaoCursor === -1) return;
    
    // Encontrar o @ antes do cursor
    const textBeforeCursor = fullText.substring(0, posicaoCursor);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex === -1) return;
    
    // Construir novo texto
    const textBeforeAt = fullText.substring(0, atIndex);
    const textAfterCursor = fullText.substring(posicaoCursor);
    const mentionText = `@[${collaborator.id}]{${collaborator.nome}}\u0001`;
    const newText = textBeforeAt + mentionText + ' ' + textAfterCursor;
    
    // Atualizar HTML
    const html = processMentionHTML(newText, 'mention-tag', currentUserId);
    editorRef.current.innerHTML = html;
    
    // Posicionar cursor após a menção + espaço
    const newCursorPos = textBeforeAt.length + mentionText.length + 1;
    restoreposicaoCursor(editorRef.current, newCursorPos);
    
    // Atualizar valor (remover delimitador antes de passar para onChange)
    onChange(newText.split('\u0001').join(''));

    setShowSuggestions(false);
    clearSuggestions();
    editorRef.current.focus();
  }, [clearSuggestions, onChange]);

  const handleInput = useCallback(() => {
    detectMention();
    const textForSave = getTextForSave();
    onChange(textForSave);
  }, [detectMention, getTextForSave, onChange]);

  // Handler de teclado para navegação
  const handleKeyDown = useCallback((e) => {
    // Tratar backspace em tags de menção
    if (e.key === 'Backspace') {
      const selection = window.getSelection();
      if (selection.rangeCount) {
        const range = selection.getRangeAt(0);
        
        if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
          const previousSibling = range.startContainer.previousSibling;
          if (previousSibling?.classList?.contains('mention-tag')) {
            e.preventDefault();
            previousSibling.remove();
            onChange(getTextForSave());
            return;
          }
        }
      }
    }

    if (!showSuggestions || !suggestions.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => prev > 0 ? prev - 1 : 0);
        break;
        
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (suggestions[selectedIndex]) {
          insertMention(suggestions[selectedIndex]);
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        clearSuggestions();
        break;
        
      default:
        break;
    }
  }, [showSuggestions, suggestions, selectedIndex, insertMention, clearSuggestions, getTextForSave, onChange]);

  // Handler de click para posicionar cursor após tags de menção
  const handleClick = useCallback((e) => {
    if (e.target.classList?.contains('mention-tag')) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.setStartAfter(e.target);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  // Handler de paste para sempre colar apenas texto puro (sem formatação)
  const handlePaste = useCallback((e) => {
    e.preventDefault();

    if (disabled) return;

    // Obter texto puro da área de transferência
    const clipboardData = e.clipboardData || window.clipboardData;
    const text = clipboardData.getData('text/plain');

    if (!text) return;

    // Obter seleção atual
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);

    // Deletar conteúdo selecionado (se houver)
    range.deleteContents();

    // Dividir o texto em linhas para preservar parágrafos
    const lines = text.split(/\r\n|\r|\n/);

    // Inserir cada linha, adicionando <br> entre elas
    lines.forEach((line, index) => {
      if (index > 0) {
        // Adicionar <br> antes de cada linha (exceto a primeira)
        const br = document.createElement('br');
        range.insertNode(br);
        range.setStartAfter(br);
      }

      // Adicionar o texto da linha
      if (line) {
        const textNode = document.createTextNode(line);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
      }
    });

    // Mover cursor para o final do texto inserido
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    // Processar o conteúdo e atualizar o onChange
    setTimeout(() => {
      const textForSave = getTextForSave();
      onChange(textForSave);
      detectMention();
    }, 0);
  }, [disabled, getTextForSave, onChange, detectMention]);

  // Sincronizar conteúdo com value prop (apenas na montagem inicial ou quando limpar)
  useEffect(() => {
    if (editorRef.current) {
      const currentText = extractTextWithMentions(editorRef.current);

      // Se o value está vazio mas o editor tem conteúdo, limpar
      if (!value && currentText) {
        editorRef.current.innerHTML = '';
      }
      // Se há value e o editor está vazio (montagem inicial), atualizar
      else if (value && !currentText) {
        const html = processMentionHTML(value, 'mention-tag', currentUserId);
        editorRef.current.innerHTML = html;
      }
    }
  }, [value]);

  return (
    <div className="mentions-container">
      <div
        ref={editorRef}
        className="mentions-editor"
        contentEditable={!disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        onPaste={handlePaste}
        style={style}
        dir="ltr"
        {...props}
      />
      {showSuggestions && (
        <MentionDropdown
          suggestions={suggestions}
          selectedIndex={selectedIndex}
          onSelect={insertMention}
          onSelectIndex={setSelectedIndex}
        />
      )}
    </div>
  );
};

export default Mentions;
